/**
 * Purpose: Shared player creation form used across all mode setup screens.
 * Owns: Player name input, age group selection, dynamic tier selection,
 *       optional major selection, add player button, and player list display.
 * Notes: Tier labels update dynamically when the age group changes (CS-21).
 *        Major selection only appears for High School (teens-13-17) and
 *        College (adults-18+) age groups. Younger groups and Parents default
 *        to 'General Ed'. Supports 1-8 players. Large tap targets for
 *        accessibility (CS-13).
 */

import { useState, useEffect } from 'react';
import type { Player, Major } from '../../types/game';
import type { AgeGroup, Tier } from '../../types/question';
import { MAX_PLAYERS, MIN_PLAYERS } from '../../config/classConfig';
import {
  AGE_GROUP_OPTIONS,
  requiresMajor,
  MAJOR_OPTIONS,
  DEFAULT_MAJOR,
  getTierLabel,
} from '../../config/modeConfig';
import { TierSelector } from './TierSelector';

interface PlayerFormProps {
  /** Current list of players already added */
  players: Player[];
  /** Called when the host adds a new player */
  onAddPlayer: (name: string, ageGroup: AgeGroup, tier: Tier, major: Major) => void;
  /** Called when a player is removed */
  onRemovePlayer: (playerId: string) => void;
  /** Called when the host is ready to start the game */
  onStartGame: () => void;
}

export function PlayerForm({
  players,
  onAddPlayer,
  onRemovePlayer,
  onStartGame,
}: PlayerFormProps) {
  const [name, setName] = useState('');
  const [ageGroup, setAgeGroup] = useState<AgeGroup>('kids-6-8');
  const [tier, setTier] = useState<Tier>('student');
  const [major, setMajor] = useState<Major>(DEFAULT_MAJOR);

  /* Reset tier to 'student' and major to default when age group changes */
  useEffect(() => {
    setTier('student');
    setMajor(DEFAULT_MAJOR);
  }, [ageGroup]);

  const canAdd = name.trim().length > 0 && players.length < MAX_PLAYERS;
  const canStart = players.length >= MIN_PLAYERS;
  const showMajor = requiresMajor(ageGroup);

  function handleAdd() {
    if (!canAdd) return;
    const playerMajor = showMajor ? major : DEFAULT_MAJOR;
    onAddPlayer(name.trim(), ageGroup, tier, playerMajor as Major);
    setName('');
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>Players</h2>
      <p style={styles.subtitle}>
        {players.length}/{MAX_PLAYERS} players added
      </p>

      {/* Player name input */}
      <div style={styles.inputRow}>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Player name"
          style={styles.input}
          maxLength={20}
          aria-label="Player name"
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
        />
      </div>

      {/* Age group selector */}
      <div style={styles.selectorGroup}>
        <label style={styles.label}>Age Group</label>
        <div style={styles.chips}>
          {AGE_GROUP_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              style={{
                ...styles.chip,
                ...(ageGroup === opt.value ? styles.chipActive : {}),
              }}
              onClick={() => setAgeGroup(opt.value)}
              aria-pressed={ageGroup === opt.value}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Dynamic tier selector */}
      <TierSelector
        ageGroup={ageGroup}
        selectedTier={tier}
        onTierChange={setTier}
      />

      {/* Major selector — only for High School and College */}
      {showMajor && (
        <div style={styles.selectorGroup}>
          <label style={styles.label}>Major</label>
          <div style={styles.chips}>
            {MAJOR_OPTIONS.map((m) => (
              <button
                key={m}
                style={{
                  ...styles.chip,
                  ...(major === m ? styles.chipActive : {}),
                }}
                onClick={() => setMajor(m as Major)}
                aria-pressed={major === m}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Add player button */}
      <button
        style={{
          ...styles.addButton,
          opacity: canAdd ? 1 : 0.5,
        }}
        onClick={handleAdd}
        disabled={!canAdd}
        aria-label="Add player"
      >
        + Add Player
      </button>

      {/* Player list */}
      {players.length > 0 && (
        <div style={styles.playerList}>
          {players.map((player) => (
            <div key={player.id} style={styles.playerRow}>
              <div style={{ ...styles.dot, backgroundColor: player.color }} />
              <span style={styles.playerName}>{player.name}</span>
              <span style={styles.playerMeta}>
                {AGE_GROUP_OPTIONS.find((a) => a.value === player.ageGroup)?.label}
                {' \u00B7 '}
                {getTierLabel(player.tier, player.ageGroup)}
                {player.major !== DEFAULT_MAJOR && ` \u00B7 ${player.major}`}
              </span>
              <button
                style={styles.removeButton}
                onClick={() => onRemovePlayer(player.id)}
                aria-label={`Remove ${player.name}`}
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Action buttons after at least one player */}
      {players.length > 0 && (
        <div style={styles.actions}>
          <button
            className="btn-gold"
            style={{
              ...styles.startButton,
              opacity: canStart ? 1 : 0.5,
            }}
            onClick={onStartGame}
            disabled={!canStart}
            aria-label="Start game"
          >
            Go to Class
          </button>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1rem',
    width: '100%',
    maxWidth: '480px',
    margin: '0 auto',
  },
  heading: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: 'var(--text-light)',
    textAlign: 'center' as const,
  },
  subtitle: {
    fontSize: '0.85rem',
    color: 'var(--muted)',
    textAlign: 'center' as const,
  },
  inputRow: {
    display: 'flex',
    gap: '0.5rem',
  },
  input: {
    flex: 1,
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    border: '2px solid var(--accent-wood)',
    backgroundColor: 'var(--bg-card)',
    color: 'var(--text-dark)',
    fontSize: '1rem',
    outline: 'none',
  },
  selectorGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
  },
  label: {
    fontSize: '0.8rem',
    fontWeight: '600',
    color: 'var(--muted)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.4rem',
  },
  chip: {
    padding: '0.5rem 0.75rem',
    borderRadius: '8px',
    backgroundColor: 'var(--bg-surface)',
    color: 'var(--text-light)',
    fontSize: '0.85rem',
    fontWeight: '500',
    border: '1px solid transparent',
    cursor: 'pointer',
  },
  chipActive: {
    backgroundColor: 'var(--accent-wood)',
    border: '1px solid var(--accent-gold)',
    color: 'var(--text-light)',
  },
  addButton: {
    padding: '0.875rem',
    borderRadius: '10px',
    backgroundColor: 'var(--accent-gold)',
    color: 'var(--text-dark)',
    fontSize: '1rem',
    fontWeight: '700',
    border: 'none',
    cursor: 'pointer',
  },
  playerList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginTop: '0.5rem',
  },
  playerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 0.75rem',
    borderRadius: '8px',
    backgroundColor: 'var(--bg-surface)',
  },
  dot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  playerName: {
    fontWeight: '600',
    color: 'var(--text-light)',
    fontSize: '0.95rem',
  },
  playerMeta: {
    flex: 1,
    fontSize: '0.75rem',
    color: 'var(--muted)',
    textAlign: 'right' as const,
  },
  removeButton: {
    width: '28px',
    height: '28px',
    minHeight: '28px',
    minWidth: '28px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.1)',
    color: 'var(--text-light)',
    fontSize: '1.1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    marginTop: '0.5rem',
  },
  startButton: {
    padding: '1rem',
    fontSize: '1.2rem',
    fontFamily: 'var(--font-family)',
    borderRadius: '12px',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 4px 0 rgba(0,0,0,0.2)',
  },
};
